import AddIcon from "@mui/icons-material/Add";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FactoryIcon from "@mui/icons-material/Factory";
import FilterCenterFocusIcon from "@mui/icons-material/FilterCenterFocus";
import MergeIcon from "@mui/icons-material/Merge";
import {
  Alert,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogContentText,
  Paper,
  Radio,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { groupBy } from "lodash-es";
import pluralize from "pluralize";

import { InventoryOverlap, InventoryOverlapEntry, inventoryOverlapKey } from "./inventoryOverlaps";

type ResolveInventoryOverlapsStepProps = {
  inventoryOverlaps: InventoryOverlap[];
  selections: Record<string, string>;
  onSelect: (key: string, keepChemicalId: string) => void;
  onBack: () => void;
  onMerge: () => void;
  isMerging: boolean;
  mergedChemicalName: string;
  affectedFacilityCount: number;
  candidateCount: number;
  autoMergeFacilityYearCount: number;
};

function formatEntryAmount(entry: Pick<InventoryOverlapEntry, "maxAmount" | "unit">): string {
  if (entry.maxAmount === null) return "—";

  const amountLabel = entry.maxAmount.toLocaleString();
  if (!entry.unit) return amountLabel;

  return `${amountLabel} ${prettyPrintEnumValue(entry.unit).toLowerCase()}`;
}

function renderStorageLocations(storageLocationNames: string[]) {
  if (storageLocationNames.length === 0) {
    return "—";
  }

  return storageLocationNames.map((storageLocationName) => (
    <Box key={storageLocationName}>{storageLocationName}</Box>
  ));
}

function renderMergedAmount(selectedEntry: InventoryOverlapEntry | undefined) {
  if (!selectedEntry) {
    return (
      <>
        {"— "}
        <Typography variant="caption" color="text.secondary">
          select a chemical to keep
        </Typography>
      </>
    );
  }

  return (
    <>
      {formatEntryAmount(selectedEntry)}
      <Typography variant="caption" color="text.secondary">
        {` from ${selectedEntry.chemicalName}`}
      </Typography>
    </>
  );
}

type InventoryOverlapCardProps = {
  inventoryOverlap: InventoryOverlap;
  selectedChemicalId: string | undefined;
  onSelect: (keepChemicalId: string) => void;
};

function InventoryOverlapCard({
  inventoryOverlap,
  selectedChemicalId,
  onSelect,
}: InventoryOverlapCardProps) {
  const key = inventoryOverlapKey(inventoryOverlap);
  const selectedEntry = inventoryOverlap.entries.find(
    (entry) => entry.chemicalId === selectedChemicalId
  );

  const columnSx = (chemicalId: string) =>
    chemicalId === selectedChemicalId ? { bgcolor: "action.selected" } : undefined;

  return (
    <Paper variant="outlined" data-testid={`inventory-overlap-${key}`} sx={{ p: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6">{inventoryOverlap.reportingYear}</Typography>
          {inventoryOverlap.hasIdenticalAmounts && (
            <Chip icon={<ContentCopyIcon />} label="identical amounts" size="small" />
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            Resolution:
          </Typography>
          <Tooltip title="Combine and Leave blank are available in an upcoming release">
            <ToggleButtonGroup
              exclusive
              size="small"
              value="KEEP"
              data-testid={`inventory-overlap-strategy-${key}`}
            >
              <ToggleButton value="KEEP">
                <FilterCenterFocusIcon fontSize="small" sx={{ mr: 0.5 }} />
                Keep one
              </ToggleButton>
              <ToggleButton value="COMBINE" disabled>
                <AddIcon fontSize="small" sx={{ mr: 0.5 }} />
                Combine
              </ToggleButton>
              <ToggleButton value="LEAVE_BLANK" disabled>
                <CheckBoxOutlineBlankIcon fontSize="small" sx={{ mr: 0.5 }} />
                Leave blank
              </ToggleButton>
            </ToggleButtonGroup>
          </Tooltip>
        </Stack>
      </Stack>
      {/* Fixed layout keeps column widths stable as selections change cell content. */}
      <Table size="small" sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: "12%" }} />
            {inventoryOverlap.entries.map((entry) => (
              <TableCell key={entry.chemicalId} sx={columnSx(entry.chemicalId)}>
                <Radio
                  checked={entry.chemicalId === selectedChemicalId}
                  onChange={() => onSelect(entry.chemicalId)}
                  inputProps={{ "aria-label": `Keep ${entry.chemicalName}` }}
                  data-testid={`inventory-overlap-keep-${key}-${entry.chemicalId}`}
                  size="small"
                />
                {entry.chemicalName}
              </TableCell>
            ))}
            <TableCell>
              <Typography variant="body2" color="text.secondary">
                → Merged inventory
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell component="th" scope="row">
              Amount
            </TableCell>
            {inventoryOverlap.entries.map((entry) => (
              <TableCell key={entry.chemicalId} sx={columnSx(entry.chemicalId)}>
                {formatEntryAmount(entry)}
              </TableCell>
            ))}
            <TableCell data-testid={`inventory-overlap-merged-amount-${key}`}>
              {renderMergedAmount(selectedEntry)}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell component="th" scope="row">
              Storage
            </TableCell>
            {inventoryOverlap.entries.map((entry) => (
              <TableCell key={entry.chemicalId} sx={columnSx(entry.chemicalId)}>
                {renderStorageLocations(entry.storageLocationNames)}
              </TableCell>
            ))}
            <TableCell data-testid={`inventory-overlap-merged-storage-${key}`}>
              {renderStorageLocations(selectedEntry?.storageLocationNames ?? [])}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Paper>
  );
}

export function ResolveInventoryOverlapsStep(props: ResolveInventoryOverlapsStepProps) {
  const allResolved = props.inventoryOverlaps.every(
    (inventoryOverlap) => !!props.selections[inventoryOverlapKey(inventoryOverlap)]
  );

  const facilityGroups = Object.values(groupBy(props.inventoryOverlaps, "facilityId"));

  return (
    <>
      {/* Match MergePickerContent's height so the dialog doesn't resize between steps. */}
      <DialogContent sx={{ height: "80vh", overflowY: "auto" }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            <b>{props.candidateCount} catalog entries → 1.</b>{" "}
            {props.inventoryOverlaps.length === 1
              ? "1 facility-year overlap needs a decision."
              : `${props.inventoryOverlaps.length} facility-years overlap and need a decision.`}
            {props.autoMergeFacilityYearCount > 0 && (
              <>
                {" "}
                {props.autoMergeFacilityYearCount === 1
                  ? "The other facility-year merges automatically."
                  : `The other ${props.autoMergeFacilityYearCount} facility-years merge automatically.`}
              </>
            )}
          </Alert>
          {facilityGroups.map((groupOverlaps) => (
            <Stack key={groupOverlaps[0].facilityId} spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FactoryIcon fontSize="small" color="action" />
                <Typography variant="subtitle1" fontWeight="bold">
                  {groupOverlaps[0].facilityName}
                </Typography>
              </Stack>
              <Stack spacing={2}>
                {groupOverlaps.map((inventoryOverlap) => {
                  const key = inventoryOverlapKey(inventoryOverlap);
                  return (
                    <InventoryOverlapCard
                      key={key}
                      inventoryOverlap={inventoryOverlap}
                      selectedChemicalId={props.selections[key]}
                      onSelect={(keepChemicalId) => props.onSelect(key, keepChemicalId)}
                    />
                  );
                })}
              </Stack>
            </Stack>
          ))}
          <DialogContentText component="div">
            <Typography component="p">
              You are about to create a new merged chemical for <i>{props.mergedChemicalName}</i>.
              This action will merge the selected chemicals into a single merged chemical in your
              chemical catalog and update all references to those chemicals in your facility
              inventory.{" "}
              <b>
                {props.affectedFacilityCount} {pluralize("facility", props.affectedFacilityCount)}{" "}
                will be affected.
              </b>
            </Typography>
            <Typography component="p" sx={{ mt: 1 }}>
              This action will <b>not</b> affect previously submitted reports. Dropped entries
              remain available in the merge audit record.
            </Typography>
          </DialogContentText>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={props.onBack}
          variant="outlined"
          data-testid="resolve-inventory-overlaps-back"
        >
          BACK
        </Button>
        <Button
          variant="contained"
          disabled={!allResolved || props.isMerging}
          loading={props.isMerging}
          onClick={props.onMerge}
          data-testid="resolve-inventory-overlaps-merge"
        >
          <MergeIcon /> MERGE
        </Button>
      </DialogActions>
    </>
  );
}
